import React, { useEffect, useState } from 'react';
import config from '../../config';
import {
  ResponsiveContainer,
  ReferenceLine,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';
import CustomTooltip from './CustomTooltip';

const ThresholdStrokeWidth = 3;
const ThresholdDashArray = '10 6';

const WSEStrokeWidthShort = 3;
const WSEStrokeWidthMedium = 2;

function getPublicationTimeStr(data) {
  if (data) {
    return config.formatDateTime(data[0].pub_time_unix_milliseconds);
  }
  return 'N/A';
}

function customTicks(forecastShort, forecastMedium) {
  const numTicks = 4;
  let listOfDateTimes = [];
  let listOfTicks = [];
  if (forecastShort) {
    forecastShort.forEach(function (entry) {
      listOfDateTimes.push(entry.forecast_time_unix_milliseconds);
    });
  }
  if (forecastMedium) {
    forecastMedium.forEach(function (entry) {
      listOfDateTimes.push(entry.forecast_time_unix_milliseconds);
    });
  }
  listOfDateTimes.sort(function (a, b) {
    return a - b;
  });
  const remainder = Math.floor(listOfDateTimes.length / numTicks);
  for (var i = 0; i < listOfDateTimes.length - 1; i++) {
    if (i === 0 || i === listOfDateTimes.length - 1) {
      listOfTicks.push(listOfDateTimes[i]);
    } else if (i % remainder === 0) {
      listOfTicks.push(listOfDateTimes[i]);
    }
  }
  // console.log(listOfTicks);
  return listOfTicks;
}

function getYMin(values) {
  return Math.floor(Math.min(...values) - 2);
}

function getYMax(values) {
  return Math.ceil(Math.max(...values) + 2);
}

export default function PlotForecast({
  locationIDs,
  forecastShort,
  forecastMedium,
  triggerLevels,
  theme
}) {
  let pubTimeShortLabel = '';
  let pubTimeMediumLabel = '';
  if (forecastShort.length > 0) {
    pubTimeShortLabel = `Short range forecast as of\n     ${getPublicationTimeStr(
      forecastShort
    )}`;
  }
  if (forecastMedium.length > 0) {
    pubTimeMediumLabel = `Medium range forecast as of\n     ${getPublicationTimeStr(
      forecastMedium
    )}`;
  }

  const [colors, setColors] = useState(config[theme + 'PlotColors']);

  useEffect(() => {
    theme === 'dark'
      ? setColors(config.darkPlotColors)
      : setColors(config.lightPlotColors);
  }, [theme]);

  return (
    <>
      <div className='plot'>
        <div>
          {forecastShort.length > 0 || forecastMedium.length > 0
            ? 'Predicted Stage at ' + locationIDs.asset
            : 'Forecast Unavailable at the Selected Time'}
        </div>
        <ResponsiveContainer width='95%' height={350}>
          <LineChart margin={{ top: 25, left: 75, right: 0, bottom: 35 }}>
            <CartesianGrid
              strokeDasharray='3 7'
              strokeWidth='0.3'
              stroke={colors.colorGrid}
            />
            <Line
              name={`Asset Elevation (${triggerLevels.ground_elevation.toFixed(
                2
              )})`}
              stroke={colors.colorGround}
            />
            <Line
              name={`Major Threshold (${triggerLevels.major.toFixed(2)})`}
              stroke={colors.colorMajor}
            />
            <Line
              name={`Moderate Threshold (${triggerLevels.moderate.toFixed(2)})`}
              stroke={colors.colorModerate}
            />
            <Line
              name={`Minor Threshold (${triggerLevels.minor.toFixed(2)}) `}
              stroke={colors.colorMinor}
            />
            <Line
              name={`Action Threshold (${triggerLevels.action.toFixed(2)})`}
              stroke={colors.colorAction}
            />
            {forecastMedium.length > 0 ? (
              <Line
                name={pubTimeMediumLabel}
                data={forecastMedium}
                dataKey='stage'
                stroke={colors.colorWSEMedium}
                dot={false}
                strokeWidth={WSEStrokeWidthMedium}
              />
            ) : null}
            {forecastShort.length > 0 ? (
              <Line
                name={pubTimeShortLabel}
                data={forecastShort}
                dataKey='stage'
                stroke={colors.colorWSEShort}
                dot={false}
                strokeWidth={WSEStrokeWidthShort}
              />
            ) : null}
            <ReferenceLine
              y={triggerLevels.major}
              stroke={colors.colorMajor}
              strokeWidth={ThresholdStrokeWidth}
              strokeDasharray={ThresholdDashArray}
            />
            <ReferenceLine
              y={triggerLevels.moderate}
              stroke={colors.colorModerate}
              strokeWidth={ThresholdStrokeWidth}
              strokeDasharray={ThresholdDashArray}
            />
            <ReferenceLine
              y={triggerLevels.minor}
              stroke={colors.colorMinor}
              strokeWidth={ThresholdStrokeWidth}
              strokeDasharray={ThresholdDashArray}
            />
            <ReferenceLine
              y={triggerLevels.action}
              stroke={colors.colorAction}
              strokeWidth={ThresholdStrokeWidth}
              strokeDasharray={ThresholdDashArray}
            />
            <ReferenceLine
              y={triggerLevels.ground_elevation}
              strokeWidth={0} // 0-width ReferenceLine so we see label, but not line
              label={{
                fill: colors.colorGround,
                value: 'Asset Elevation 🡇',
                fontSize: 12,
                fontWeight: 'bold',
                position: 'insideLeft',
                dy: -8
              }}
            />
            <XAxis
              dataKey='forecast_time_unix_milliseconds'
              type='number'
              textAnchor='end'
              domain={['dataMin', 'dataMax']}
              interval={() => {
                if (forecastShort.length > 0 || forecastMedium.length > 0) {
                  return 0;
                } else {
                  return null;
                }
              }}
              tick={{ angle: -10, fontSize: 13, fontWeight: 'bold' }}
              tickFormatter={(datetime) => config.formatDateTime(datetime)}
              ticks={customTicks(forecastShort, forecastMedium)}
              label={{
                value: `Time (${config.localeTZShortCode()})`,
                dy: 36,
                fill: colors.colorAxis,
                fontSize: 14,
                fontWeight: 'bold'
              }}
            />
            <YAxis
              dataKey='stage'
              domain={[
                (dataMin) =>
                  getYMin([
                    dataMin,
                    triggerLevels.action,
                    triggerLevels.ground_elevation
                  ]),
                (dataMax) =>
                  getYMax([
                    dataMax,
                    triggerLevels.major,
                    triggerLevels.ground_elevation
                  ])
              ]}
              tick={{ fontSize: 16, fontWeight: 'bold' }}
              tickFormatter={(value) => parseFloat(value).toFixed(1)}
              label={{
                value: 'Elevation (ft)',
                dx: -50,
                angle: -90,
                fill: colors.colorAxis,
                fontSize: 16,
                fontWeight: 'bold'
              }}
            />
            <Legend
              layout='vertical'
              verticalAlign='top'
              align='right'
              wrapperStyle={{
                fontSize: 14,
                fontWeight: 'bold',
                paddingLeft: 14,
                paddingTop: 16,
                width: 230,
                whiteSpace: 'break-spaces'
              }}
            />
            <Tooltip content={<CustomTooltip />} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}
